import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Staff, StaffEmploymentInfo } from '../../models/StaffModels';
import { useStaff } from '../../contexts/StaffContext';
import '../../styles/staff-forms-modals.css';

const StaffForm = ({ 
  staff = null, 
  departments = [], 
  onSubmit, 
  isLoading = false, 
  error = null 
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('staff');
  const { isPinTakenInDepartments } = useStaff();
  const isEditMode = !!staff;
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    pin: '0000',
    role: 'staff',
    primaryDepartmentId: '',
    departmentIds: [],
    startDate: new Date().toISOString().split('T')[0]
  });
  
  // Vacation account setup state (only for new staff)
  const [vacationFormData, setVacationFormData] = useState({
    baseEntitlement: 0,
    carryOverLimit: 0,
    carryOverDeadline: `${new Date().getFullYear()}-03-31`,
    autoCalculateCarryover: false
  });
  
  const [formErrors, setFormErrors] = useState({});

  // Tracks the async PIN-uniqueness check (Firestore round trip) that runs
  // on submit before we persist anything. Used to disable the submit
  // button and avoid double-submits while the check is in flight.
  const [isCheckingPin, setIsCheckingPin] = useState(false);
  
  // Initialize form with staff data if editing
  useEffect(() => {
    if (staff) {
      setFormData({
        name: staff.name || '',
        email: staff.email || '',
        pin: '', // Never pre-fill PIN field for security - consistent with StaffDetailPage
        role: staff.role || 'staff',
        primaryDepartmentId: staff.primaryDepartmentId || '',
        departmentIds: staff.departmentIds || [],
        startDate: staff.employmentInfo?.startDate 
          ? staff.employmentInfo.startDate.toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0]
      });
    }
  }, [staff]);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error for this field when it's changed
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  // Handle department selection changes
  const handleDepartmentChange = (e) => {
    const { options } = e.target;
    const selectedDepartments = Array.from(options)
      .filter(option => option.selected)
      .map(option => option.value);
    
    setFormData(prev => ({
      ...prev,
      departmentIds: selectedDepartments
    }));
    
    // Clear error for departments if it exists
    if (formErrors.departmentIds) {
      setFormErrors(prev => ({ ...prev, departmentIds: null }));
    }
  };
  
  // Handle vacation account input changes
  const handleVacationInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setVacationFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field when it's changed
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  // Enhanced validation to match StaffDetailPage standards
  const validateForm = () => {
    const errors = {};
    
    // Name validation - consistent with StaffDetailPage
    if (!formData.name.trim()) {
      errors.name = t('validation.nameRequired') || 'Name is required';
    }
    
    // Email validation - consistent with StaffDetailPage
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('validation.emailInvalid') || 'Please enter a valid email address';
    }
    
    // PIN validation - consistent with StaffDetailPage (4-digit requirement)
    if (!isEditMode) {
      // For new staff, PIN is required
      if (!/^\d{4}$/.test(formData.pin)) {
        errors.pin = t('validation.pinRequired') || 'PIN must be exactly 4 digits';
      }
    } else {
      // For editing, PIN is optional but if provided must be valid
      if (formData.pin.trim() && !/^\d{4}$/.test(formData.pin.trim())) {
        errors.pin = t('validation.pinInvalid') || 'PIN must be exactly 4 digits';
      }
    }
    
    // Role validation
    if (!formData.role) {
      errors.role = t('validation.roleRequired') || 'Role is required';
    }
    
    // Department validation - enhanced logic
    if (formData.primaryDepartmentId && !formData.departmentIds.includes(formData.primaryDepartmentId)) {
      errors.primaryDepartmentId = t('validation.primaryDepartmentInvalid') || 'Primary department must be included in assigned departments';
    }
    
    // Start date validation
    if (!formData.startDate) {
      errors.startDate = t('validation.startDateRequired') || 'Start date is required';
    }
    
    // Vacation account validation (only for new staff)
    if (!isEditMode) {
      const baseEntitlement = parseFloat(vacationFormData.baseEntitlement);
      if (isNaN(baseEntitlement) || baseEntitlement < 0) {
        errors.baseEntitlement = t('vacationAccount.validation.baseEntitlementPositive') || 'Base entitlement must be 0 or positive';
      }
      
      const carryOverLimit = parseFloat(vacationFormData.carryOverLimit);
      if (isNaN(carryOverLimit) || carryOverLimit < 0) {
        errors.carryOverLimit = t('vacationAccount.validation.carryoverLimitPositive') || 'Carryover limit must be 0 or positive';
      }
      
      if (!vacationFormData.carryOverDeadline) {
        errors.carryOverDeadline = t('vacationAccount.validation.carryoverDeadlineRequired') || 'Carryover deadline is required';
      }
    }
    
    return errors;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate the form (synchronous field-level checks)
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Ensure primary department is also in departmentIds - consistent with StaffDetailPage
    // Computed here (before the PIN check) because the duplicate-PIN check
    // needs the final, merged department list to know which colleagues to
    // compare against.
    let departmentIds = [...formData.departmentIds];
    if (formData.primaryDepartmentId && !departmentIds.includes(formData.primaryDepartmentId)) {
      departmentIds.push(formData.primaryDepartmentId);
    }

    // PIN duplicate check - only runs when a PIN is actually being set.
    // Create mode: always present (required by validateForm above).
    // Edit mode: blank means "keep current PIN" -> nothing to check.
    const pinToCheck = formData.pin.trim();
    if (pinToCheck) {
      setIsCheckingPin(true);
      try {
        const conflict = await isPinTakenInDepartments(
          pinToCheck,
          departmentIds,
          staff?.id || null
        );

        if (conflict) {
          setFormErrors(prev => ({
            ...prev,
            pin: t('validation.pinTakenInDepartment', { name: conflict.name }) ||
              `PIN already used in this department by ${conflict.name}`
          }));
          return;
        }
      } catch (err) {
        console.error('Error checking PIN uniqueness:', err);
        setFormErrors(prev => ({
          ...prev,
          pin: t('validation.pinCheckFailed') || 'Could not verify PIN uniqueness. Please try again.'
        }));
        return;
      } finally {
        setIsCheckingPin(false);
      }
    }
    
    // Create employment info (contractHours/workSchedule removed - Schedule Planner is source of truth)
    const employmentInfo = new StaffEmploymentInfo(
      new Date(formData.startDate),
      null, // contractHours no longer collected
      {}, // Default empty vacation days
      null // Default work schedule - removed, no longer used
    );
    
    // Prepare staff data for submission
    const staffData = {
      id: staff?.id || null,
      name: formData.name.trim(),
      email: formData.email.trim() || null,
      role: formData.role,
      primaryDepartmentId: formData.primaryDepartmentId || null,
      departmentIds: departmentIds,
      employmentInfo: employmentInfo
    };
    
    // Only include PIN if it was entered (consistent with StaffDetailPage logic)
    if (formData.pin.trim()) {
      staffData.pin = formData.pin.trim();
    }
    
    // Create Staff instance for submission
    const staffInstance = new Staff(
      staffData.id,
      staffData.name,
      staffData.pin || null,
      staffData.departmentIds,
      staffData.primaryDepartmentId,
      staffData.email,
      staffData.role,
      staffData.employmentInfo
    );
    
    // Add vacation data to submission (only for new staff)
    const submissionData = {
      staff: staffInstance,
      vacationData: !isEditMode ? vacationFormData : null
    };
    
    // Submit the form
    onSubmit(submissionData);
  };
  
  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };
  
  return (
    <div className="staff-form-container">
      <form onSubmit={handleSubmit} className="staff-form">
        <h2>{isEditMode ? t('form.editStaff') : t('form.addNewStaff')}</h2>
        
        {/* Note about editing workflow for clarity */}
        {isEditMode && (
          <div className="info-notice">
            <p><strong>Note:</strong> For detailed editing with tabs (Personal Info, Departments, etc.), 
            use the staff detail page. This form provides basic information editing only.</p>
          </div>
        )}
        
        {error && <div className="form-error">{error}</div>}
        
        <div className="form-section">
          <h3>{t('form.personalInformation')}</h3>
          
          <div className="form-group">
            <label htmlFor="name">{t('form.name')}*</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={formErrors.name ? 'error' : ''}
              required
            />
            {formErrors.name && <div className="field-error">{formErrors.name}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="email">{t('form.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={formErrors.email ? 'error' : ''}
              placeholder="optional"
            />
            {formErrors.email && <div className="field-error">{formErrors.email}</div>}
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="pin">
                {t('form.pin')}{!isEditMode && '*'}
              </label>
              <input
                type="password"
                id="pin"
                name="pin"
                value={formData.pin}
                onChange={handleInputChange}
                pattern="\d{4}"
                maxLength="4"
                className={formErrors.pin ? 'error' : ''}
                placeholder={isEditMode ? "Leave blank to keep current PIN" : "Enter 4-digit PIN"}
                required={!isEditMode}
              />
              {formErrors.pin && <div className="field-error">{formErrors.pin}</div>}
              <small>
                {isEditMode 
                  ? t('help.pinEditHelp') || "Leave blank to keep current PIN unchanged"
                  : t('help.pinHelp') || "Must be exactly 4 digits"
                }
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="role">{t('form.role')}*</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className={formErrors.role ? 'error' : ''}
                required
              >
                <option value="staff">{t('roles.staff')}</option>
                <option value="manager">{t('roles.manager')}</option>
                <option value="admin">{t('roles.admin')}</option>
              </select>
              {formErrors.role && <div className="field-error">{formErrors.role}</div>}
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h3>{t('form.departmentAssignment')}</h3>
          
          {departments.length === 0 ? (
            <div className="no-departments-warning">
              <p><strong>Warning:</strong> No departments are available. Please create departments first before assigning staff.</p>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="departmentIds">{t('form.assignedDepartments')}</label>
                <select
                  id="departmentIds"
                  name="departmentIds"
                  multiple
                  value={formData.departmentIds}
                  onChange={handleDepartmentChange}
                  className={`multiselect ${formErrors.departmentIds ? 'error' : ''}`}
                >
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {formErrors.departmentIds && (
                  <div className="field-error">{formErrors.departmentIds}</div>
                )}
                <small>{t('help.departmentHelp') || "Hold Ctrl/Cmd to select multiple departments"}</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="primaryDepartmentId">{t('form.primaryDepartment')}</label>
                <select
                  id="primaryDepartmentId"
                  name="primaryDepartmentId"
                  value={formData.primaryDepartmentId}
                  onChange={handleInputChange}
                  className={formErrors.primaryDepartmentId ? 'error' : ''}
                >
                  <option value="">{t('form.selectPrimaryDepartment') || "Select Primary Department"}</option>
                  {departments
                    .filter(dept => formData.departmentIds.includes(dept.id))
                    .map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                </select>
                {formErrors.primaryDepartmentId && (
                  <div className="field-error">{formErrors.primaryDepartmentId}</div>
                )}
                <small>{t('help.primaryDepartmentHelp') || "Must be one of the assigned departments"}</small>
              </div>
            </>
          )}
        </div>
        
        <div className="form-section">
          <h3>{t('form.employmentDetails')}</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startDate">{t('form.startDate')}*</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={formErrors.startDate ? 'error' : ''}
                required
              />
              {formErrors.startDate && <div className="field-error">{formErrors.startDate}</div>}
            </div>
          </div>
        </div>
        
        {/* Vacation Account Setup Section - Only for new staff */}
        {!isEditMode && (
          <div className="form-section">
            <h3>{t('form.vacationAccountSetup') || 'Vacation Account Setup (Current Year)'}</h3>
            
            {/* Base Entitlement */}
            <div className="form-group">
              <label htmlFor="baseEntitlement">
                {t('vacationAccount.baseEntitlement')}*
              </label>
              <div className="input-with-unit">
                <input
                  type="number"
                  id="baseEntitlement"
                  name="baseEntitlement"
                  value={vacationFormData.baseEntitlement}
                  onChange={handleVacationInputChange}
                  min="0"
                  step="0.5"
                  className={formErrors.baseEntitlement ? 'error' : ''}
                />
                <span className="unit">{t('vacationAccount.days') || 'days'}</span>
              </div>
              {formErrors.baseEntitlement && (
                <div className="field-error">{formErrors.baseEntitlement}</div>
              )}
              <small>{t('vacationAccount.baseEntitlementHint')}</small>
            </div>
            
            {/* Carryover Limit */}
            <div className="form-group">
              <label htmlFor="carryOverLimit">
                {t('vacationAccount.carryoverLimit')}*
              </label>
              <div className="input-with-unit">
                <input
                  type="number"
                  id="carryOverLimit"
                  name="carryOverLimit"
                  value={vacationFormData.carryOverLimit}
                  onChange={handleVacationInputChange}
                  min="0"
                  step="0.5"
                  className={formErrors.carryOverLimit ? 'error' : ''}
                />
                <span className="unit">{t('vacationAccount.days') || 'days'}</span>
              </div>
              {formErrors.carryOverLimit && (
                <div className="field-error">{formErrors.carryOverLimit}</div>
              )}
              <small>{t('vacationAccount.carryoverLimitHint')}</small>
            </div>
            
            {/* Carryover Deadline */}
            <div className="form-group">
              <label htmlFor="carryOverDeadline">
                {t('vacationAccount.carryoverDeadline', { 
                  previousYear: new Date().getFullYear() - 1,
                  currentYear: new Date().getFullYear()
                })}*
              </label>
              <input
                type="date"
                id="carryOverDeadline"
                name="carryOverDeadline"
                value={vacationFormData.carryOverDeadline}
                onChange={handleVacationInputChange}
                className={formErrors.carryOverDeadline ? 'error' : ''}
              />
              {formErrors.carryOverDeadline && (
                <div className="field-error">{formErrors.carryOverDeadline}</div>
              )}
              <small>
                {t('vacationAccount.carryoverDeadlineHint', {
                  previousYear: new Date().getFullYear() - 1,
                  currentYear: new Date().getFullYear()
                })}
              </small>
            </div>
            
            {/* Auto Calculate Carryover */}
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="autoCalculateCarryover"
                  checked={vacationFormData.autoCalculateCarryover}
                  onChange={handleVacationInputChange}
                />
                <span>{t('vacationAccount.autoCalculateCarryover')}</span>
              </label>
              <small>{t('vacationAccount.autoCalculateCarryoverHint')}</small>
            </div>
            
            <div className="info-text">
              <p>
                <strong>{t('help.note')}:</strong>{' '}
                {t('help.vacationAccountNote', { year: new Date().getFullYear() })}
              </p>
            </div>
          </div>
        )}
        
        <div className="form-actions">
          <button 
            type="button" 
            onClick={handleCancel} 
            className="cancel-button"
            disabled={isLoading || isCheckingPin}
          >
            {t('buttons.cancel')}
          </button>
          <button 
            type="submit" 
            className="submit-button"
            disabled={isLoading || isCheckingPin || (departments.length === 0)}
          >
            {isCheckingPin
              ? (t('buttons.checkingPin') || 'Checking PIN...')
              : isLoading 
                ? (t('buttons.saving') || 'Saving...') 
                : isEditMode 
                  ? (t('buttons.saveChanges') || 'Save Changes')
                  : (t('buttons.createStaffMember') || 'Create Staff Member')
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default StaffForm;